import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';

import VideoCreativeService from 'services/VideoCreativeService';
import AdDetailDialog from './AdDetailDialog';
import FallbackVideo from './FallbackVideo';
import TagEditor from 'shared/components/TagEditor';
import CommentsSection from './CommentsSection';
import PromptModelDialog from './PromptModelDialog';
import { ScriptPdfReference, ScriptPdfKeyframe } from './generateScriptPdf';
import ScriptBriefDialog from './ScriptBriefDialog';
import SendToCreatorDialog from './SendToCreatorDialog';
import DeliveriesDialog from './DeliveriesDialog';
import { fetchImageForPdf } from './pdfHelpers';
import useSortableTable from 'shared/useSortableTable';
import { Storyboard, StoryboardComment, StoryboardHistoryRow, StoryboardProductSummary, StoryboardSegment, StoryboardSegmentShot } from './types';
import { MODEL_OPTIONS, DEFAULT_GENERATION_MODEL, coerceModelOption } from './models';
import { groupShotsIntoClips, applyClipActions, clipSpecs, formatShotRole } from './clipView';
import { shouldPoll, isPollingPaused } from 'shared/pollingGate';
import { formatScriptForLLM, formatStoryboardForLLM } from './exportHelpers';
import { Keyframe, getCachedKeyframes, setCachedKeyframes } from './keyframeCache';

const service = new VideoCreativeService();
const PAGE_SIZE = 20;

function formatEnumLabel(val: string | null | undefined): string {
  if (!val) return '';
  return String(val).replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());
}

function formatDateShort(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

// CI_STORYBOARDS.status → display label / chip color.
const STATUS_LABELS: Record<string, string> = {
  SAVED: 'Saved', GENERATING: 'Generating', DONE: 'Generated', FAILED: 'Failed',
};
function statusLabel(s: string): string { return STATUS_LABELS[s] || s; }
function statusColor(s: string): 'success' | 'error' | 'warning' | 'info' | 'default' {
  if (s === 'DONE') return 'success';
  if (s === 'FAILED') return 'error';
  if (s === 'GENERATING') return 'warning';
  if (s === 'SAVED') return 'info';
  return 'default';
}

type RefStatus = { transcriptionStatus: string | null; extractionStatus: string | null };

// Pick the earliest-in-progress pipeline stage across a storyboard's
// reference ads. Returned label drives the sub-chip shown next to the
// row's main status. Returns null when the row isn't waiting on a
// pipeline (caller falls back to the regular status label).
function deriveRefPipelineStage(
  row: StoryboardHistoryRow,
  refStatuses: RefStatus[],
): string | null {
  if (row.status !== 'GENERATING' && !(row.status === 'SAVED' && row.autoGenerateWhenReady)) return null;
  if (row.status === 'GENERATING') return 'Generating script';
  // SAVED + autoGenerateWhenReady → derive from refs.
  if (refStatuses.length === 0) return null;
  const inProgress = (s: string | null) => s === 'QUEUED' || s === 'PENDING' || s === 'PROCESSING';
  if (refStatuses.some(r => inProgress(r.transcriptionStatus))) return 'Transcribing';
  // Trans DONE + extraction not yet started ≈ analysis phase (pipeline
  // runs transcribe → analyze → extract; analysis lives in its own table,
  // not on the ad row, so this is the practical proxy).
  if (refStatuses.some(r => r.transcriptionStatus === 'DONE' && !r.extractionStatus)) return 'Analyzing';
  if (refStatuses.some(r => inProgress(r.extractionStatus))) return 'Extracting';
  if (refStatuses.every(r => r.transcriptionStatus === 'DONE' && r.extractionStatus === 'DONE')) return 'Queued for generation';
  return null;
}

export default function GeneratedAdsHistory() {
  // Table state
  const [rows, setRows] = useState<StoryboardHistoryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [filterText, setFilterText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Product filter dropdown — populated from GET /storyboards/products
  // (only products that actually have storyboards). `null` = no filter.
  const [productFilter, setProductFilter] = useState<StoryboardProductSummary | null>(null);
  const [productOptions, setProductOptions] = useState<StoryboardProductSummary[]>([]);

  // Free-form tag filter (match-any) + distinct tags for the dropdown/editor.
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [tagOptions, setTagOptions] = useState<string[]>([]);

  // Per-row expansion state: which storyboardIds are currently expanded
  // (a Set so several rows — e.g. a script and its variants — can be open
  // side-by-side), and a cache of the heavy detail we've fetched lazily so
  // re-expanding doesn't refetch.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [detailCache, setDetailCache] = useState<Record<string, Storyboard>>({});
  const [detailLoading, setDetailLoading] = useState<Record<string, boolean>>({});
  const [detailError, setDetailError] = useState<Record<string, string>>({});

  // Delete-confirm dialog state.
  const [deleteTarget, setDeleteTarget] = useState<StoryboardHistoryRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // For AdDetailDialog (click a base-ad chip to open the existing ad detail).
  const [selectedAdId, setSelectedAdId] = useState<string | null>(null);

  // storyboardId currently being generated from a SAVED row (button spinner).
  const [generatingId, setGeneratingId] = useState<string | null>(null);

  // Generate dialog (the ▶ quick action): edit the prompt + pick the model
  // before running an existing SAVED/FAILED script. genOrigModel tracks the
  // loaded model so we only PATCH it when changed; the prompt shown is always
  // persisted so generation re-uses it verbatim (never re-assembles a new one).
  const [genOpen, setGenOpen] = useState(false);
  const [genStoryboardId, setGenStoryboardId] = useState<string | null>(null);
  const [genIsRetry, setGenIsRetry] = useState(false);
  const [genPrompt, setGenPrompt] = useState('');
  const [genModel, setGenModel] = useState<string>(DEFAULT_GENERATION_MODEL);
  const [genOrigModel, setGenOrigModel] = useState<string>(DEFAULT_GENERATION_MODEL);
  const [genLoading, setGenLoading] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);

  // Multi-select state for mass-trigger / mass-delete.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState<'generate' | 'delete' | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);

  // Snackbar shown when Generate is clicked on a row whose templates need
  // the pipeline run first (the chain kicks off automatically).
  const [pipelineNoticeMsg, setPipelineNoticeMsg] = useState<string | null>(null);

  // Creator-brief PDF dialog (DONE scripts). Opened from the row quick action
  // or the expanded detail; loads the script detail + reference media (incl.
  // base64 keyframes) so the dialog can preview/edit + download.
  const [briefOpen, setBriefOpen] = useState(false);
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefStoryboard, setBriefStoryboard] = useState<Storyboard | null>(null);
  const [briefRefs, setBriefRefs] = useState<ScriptPdfReference[]>([]);
  const [briefScript, setBriefScript] = useState('');
  const [briefProductName, setBriefProductName] = useState<string | null>(null);

  // "Send to creator" dialog — same loaded bundle as the brief dialog, plus a
  // creator picker + script name. Separate state so both dialogs can coexist.
  const [sendOpen, setSendOpen] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendStoryboard, setSendStoryboard] = useState<Storyboard | null>(null);
  const [sendRefs, setSendRefs] = useState<ScriptPdfReference[]>([]);
  const [sendScript, setSendScript] = useState('');
  const [sendProductName, setSendProductName] = useState<string | null>(null);
  const [sendStoryboardId, setSendStoryboardId] = useState<string | null>(null);

  // "Delivered (N)" badge → deliveries pop-up.
  const [deliveriesOpen, setDeliveriesOpen] = useState(false);
  const [deliveriesStoryboardId, setDeliveriesStoryboardId] = useState<string | null>(null);

  // Per-ref-ad pipeline status snapshot. Drives the "Transcribing / Analyzing
  // / Extracting / Queued for generation" sub-label on rows waiting on the
  // pipeline. Keyed by adId so storyboards that share templates share the
  // same fetch.
  const [refAdStatus, setRefAdStatus] = useState<Record<string, { transcriptionStatus: string | null; extractionStatus: string | null }>>({});

  // templateAdId filter is driven by the URL query string so deep links
  // (e.g. from the Fetched Ads "Scripts" column) work without lifting state.
  // Clearing the filter pushes a new URL without the param.
  const location = useLocation();
  const history = useHistory();
  const templateAdId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get('templateAdId');
    return id && id.trim() ? id : null;
  }, [location.search]);
  const clearTemplateFilter = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete('templateAdId');
    const next = params.toString();
    history.push(next ? `${location.pathname}?${next}` : location.pathname);
  }, [history, location.pathname, location.search]);

  const load = useCallback(async (nextPage: number, productId?: number, opts?: { silent?: boolean; templateAdId?: string | null }) => {
    if (!opts?.silent) setLoading(true);
    setError(null);
    const result = await service.listAllStoryboards({
      limit: PAGE_SIZE,
      offset: nextPage * PAGE_SIZE,
      productId,
      templateAdId: opts?.templateAdId || undefined,
      ...(tagFilter.length ? { tag: tagFilter } : {}),
    });
    if (result) {
      setRows(result.storyboards || []);
      setTotal(result.total || 0);
    } else {
      setError('Failed to load generated ads.');
    }
    if (!opts?.silent) setLoading(false);
  }, [tagFilter]);

  // Re-load when product OR template-ad filter changes. Also reset page to 0
  // when the template filter changes so the user sees the first page of the
  // newly-filtered list.
  useEffect(() => {
    setPage(0);
    load(0, productFilter?.productId, { templateAdId });
  }, [load, productFilter, templateAdId]);

  // Collect ref-ad IDs across rows waiting on the pipeline or currently
  // generating. The string key (sorted, joined) lets the polling effect
  // depend on the *content* of the set instead of its reference — silent
  // reloads produce new rows arrays each tick, so a Set-ref dep would
  // restart the interval on every reload.
  const refAdIds = useMemo(() => {
    const ids = new Set<string>();
    for (const row of rows) {
      const isActive = row.status === 'GENERATING' || (row.status === 'SAVED' && row.autoGenerateWhenReady);
      if (!isActive) continue;
      for (const adId of row.referenceAdIds || []) ids.add(adId);
    }
    return Array.from(ids).sort();
  }, [rows]);
  const refAdIdsKey = refAdIds.join('|');

  // True while any visible row is still moving through the pipeline or
  // generation. Drives the auto-refresh below (even for active rows with no
  // reference ads, e.g. variants) and the "live updates paused" hint.
  const hasActiveRows = useMemo(
    () => rows.some(r => r.status === 'GENERATING' || (r.status === 'SAVED' && r.autoGenerateWhenReady)),
    [rows],
  );

  // Fetch ref-ad statuses (deduped) and silently re-load the storyboard list
  // on a 5s cadence while any row is non-terminal, so SAVED → GENERATING →
  // DONE transitions surface automatically.
  useEffect(() => {
    if (refAdIds.length === 0 && !hasActiveRows) return;
    let cancelled = false;

    const refresh = async () => {
      if (refAdIds.length === 0) return;
      const results = await Promise.all(refAdIds.map(id => service.getAd(id)));
      if (cancelled) return;
      setRefAdStatus(prev => {
        const next = { ...prev };
        for (let i = 0; i < refAdIds.length; i++) {
          const r = results[i];
          if (r) next[refAdIds[i]] = { transcriptionStatus: r.transcriptionStatus, extractionStatus: r.extractionStatus };
        }
        return next;
      });
    };

    refresh();
    const intervalId = setInterval(() => {
      // Gate: skip the Snowflake-backed status refresh + list reload while the tab
      // is hidden or the user has been idle >5 min. Resumes on activity.
      if (!shouldPoll()) return;
      refresh();
      load(page, productFilter?.productId, { silent: true, templateAdId });
    }, 5000);
    return () => { cancelled = true; clearInterval(intervalId); };
  // refAdIdsKey is the content-stable dep for refAdIds.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refAdIdsKey, hasActiveRows, load, page, productFilter, templateAdId]);

  // Surface when the polling gate has paused live updates (hidden tab /
  // idle user), so an in-progress row doesn't look silently stuck. Checked
  // on a cheap local timer — no network involved.
  const [pollingPaused, setPollingPaused] = useState(false);
  useEffect(() => {
    if (!hasActiveRows) {
      setPollingPaused(false);
      return;
    }
    const id = setInterval(() => setPollingPaused(isPollingPaused()), 2000);
    return () => clearInterval(id);
  }, [hasActiveRows]);

  // Invalidate cached detail when the polled list shows a status change.
  // Without this, a row expanded while GENERATING (segments=[] / flatScript=null)
  // would still show empty content after the status flipped to DONE — the
  // list refresh updates `rows` but the per-row detail cache is sticky.
  // If the row is currently expanded, refetch in place so the user sees the
  // new content immediately instead of having to collapse and re-expand.
  useEffect(() => {
    const stale: string[] = [];
    for (const row of rows) {
      const cached = detailCache[row.storyboardId];
      if (cached && cached.status !== row.status) stale.push(row.storyboardId);
    }
    if (stale.length === 0) return;
    setDetailCache(prev => {
      const next = { ...prev };
      for (const id of stale) delete next[id];
      return next;
    });
    for (const staleId of stale) {
      if (!expandedIds.has(staleId)) continue;
      (async () => {
        const result = await service.getStoryboard(staleId);
        if (result?.storyboard) {
          setDetailCache(p => ({ ...p, [staleId]: result.storyboard }));
        }
      })();
    }
  // detailCache deliberately omitted from deps — we only react to row updates,
  // not to our own writes (which would loop).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, expandedIds]);

  // One-shot fetch of the product filter options on mount. Refetched
  // after delete so a product whose last storyboard was removed drops
  // out of the dropdown.
  const refreshProductOptions = useCallback(async () => {
    const result = await service.listStoryboardProducts();
    if (result?.products) setProductOptions(result.products);
  }, []);
  useEffect(() => { refreshProductOptions(); }, [refreshProductOptions]);

  // Distinct script tags for the filter dropdown + tag-editor suggestions.
  const refreshTagOptions = useCallback(async () => {
    const res = await service.listStoryboardTags();
    if (res) setTagOptions(res.tags);
  }, []);
  useEffect(() => { refreshTagOptions(); }, [refreshTagOptions]);

  const handlePageChange = (next: number) => {
    setPage(next);
    setExpandedIds(new Set());
    load(next, productFilter?.productId, { templateAdId });
  };

  const handleProductFilterChange = (next: StoryboardProductSummary | null) => {
    setProductFilter(next);
    setPage(0);
    setExpandedIds(new Set());
  };

  const handleTagFilterChange = (next: string[]) => {
    setTagFilter(next);
    setPage(0);
    setExpandedIds(new Set());
  };

  // Persist a tag add/remove for one script and reflect it in local row state.
  const handleAddTag = async (storyboardId: string, tag: string) => {
    const res = await service.addStoryboardTag(storyboardId, tag);
    if (res) {
      setRows(prev => prev.map(r => r.storyboardId === storyboardId ? { ...r, tags: res.tags } : r));
      setTagOptions(prev => Array.from(new Set([...prev, ...res.tags])).sort());
    }
  };
  const handleRemoveTag = async (storyboardId: string, tag: string) => {
    const res = await service.removeStoryboardTag(storyboardId, tag);
    if (res) setRows(prev => prev.map(r => r.storyboardId === storyboardId ? { ...r, tags: res.tags } : r));
  };

  // Comments live on the lazily-loaded detail (detailCache), not on the list
  // rows. Each mutation returns the full server list; replace it in the cache.
  // Throw on a null response so CommentsSection can show an inline error.
  const applyComments = (storyboardId: string, comments: StoryboardComment[]) => {
    setDetailCache(prev => prev[storyboardId]
      ? { ...prev, [storyboardId]: { ...prev[storyboardId], comments } }
      : prev);
  };
  const handleAddComment = async (storyboardId: string, body: string) => {
    const res = await service.addStoryboardComment(storyboardId, body);
    if (!res) throw new Error('Failed to add comment.');
    applyComments(storyboardId, res.comments);
  };
  const handleUpdateComment = async (storyboardId: string, commentId: string, body: string) => {
    const res = await service.updateStoryboardComment(storyboardId, commentId, body);
    if (!res) throw new Error('Failed to update comment.');
    applyComments(storyboardId, res.comments);
  };
  const handleDeleteComment = async (storyboardId: string, commentId: string) => {
    const res = await service.deleteStoryboardComment(storyboardId, commentId);
    if (!res) throw new Error('Failed to delete comment.');
    applyComments(storyboardId, res.comments);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    const ok = await service.deleteStoryboard(deleteTarget.storyboardId);
    setDeleting(false);
    if (!ok) {
      setDeleteError('Failed to delete the storyboard.');
      return;
    }
    // Optimistically remove from the visible rows and any cached detail
    // so the UI is immediately consistent without waiting for a refetch.
    const removedId = deleteTarget.storyboardId;
    setRows(prev => prev.filter(r => r.storyboardId !== removedId));
    setTotal(t => Math.max(0, t - 1));
    setDetailCache(prev => { const n = { ...prev }; delete n[removedId]; return n; });
    setExpandedIds(prev => { const n = new Set(prev); n.delete(removedId); return n; });
    setDeleteTarget(null);
    // Refresh the product-filter dropdown counts.
    refreshProductOptions();
  };

  // Mass operations: which currently-visible rows are selected.
  const visibleIds = useMemo(() => rows.map(r => r.storyboardId), [rows]);
  const selectedCount = selectedIds.size;
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some(id => selectedIds.has(id));

  const toggleRow = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    setSelectedIds(prev => {
      if (allVisibleSelected) {
        const next = new Set(prev);
        for (const id of visibleIds) next.delete(id);
        return next;
      }
      const next = new Set(prev);
      for (const id of visibleIds) next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  // Bulk-generate: fire /generate for every selected row in turn. Rows that
  // are already DONE or GENERATING are skipped server-side (the backend's
  // markGenerating WHERE clause rejects them); the loop still returns
  // success since the call itself succeeds.
  const handleBulkGenerate = async () => {
    if (selectedCount === 0) return;
    setBulkBusy('generate');
    setBulkError(null);
    const ids = [...selectedIds];
    let queued = 0;
    let waiting = 0;
    for (const id of ids) {
      const result = await service.generateSavedStoryboard(id);
      if (!result) continue;
      if (result.adsNeedingPipeline && result.adsNeedingPipeline.length > 0) waiting++;
      else queued++;
    }
    setBulkBusy(null);
    const parts: string[] = [];
    if (queued) parts.push(`${queued} queued`);
    if (waiting) parts.push(`${waiting} waiting for pipeline`);
    setPipelineNoticeMsg(parts.length > 0 ? `Bulk generate: ${parts.join(', ')}.` : 'No rows were eligible.');
    // Drop cached details for everything we touched so re-expand refetches.
    setDetailCache(prev => {
      const next = { ...prev };
      for (const id of ids) delete next[id];
      return next;
    });
    clearSelection();
    load(page, productFilter?.productId, { templateAdId });
  };

  // Bulk-delete: sequential calls to the existing /storyboards/:id DELETE.
  // Confirmation happens in the bulkDeleteOpen dialog before this fires.
  const handleBulkDeleteConfirm = async () => {
    if (selectedCount === 0) return;
    setBulkBusy('delete');
    setBulkError(null);
    const ids = [...selectedIds];
    const failed: string[] = [];
    for (const id of ids) {
      const ok = await service.deleteStoryboard(id);
      if (!ok) failed.push(id);
    }
    setBulkBusy(null);
    if (failed.length > 0) {
      setBulkError(`${failed.length} of ${ids.length} could not be deleted.`);
      return;
    }
    setRows(prev => prev.filter(r => !selectedIds.has(r.storyboardId)));
    setTotal(t => Math.max(0, t - (ids.length - failed.length)));
    setDetailCache(prev => {
      const next = { ...prev };
      for (const id of ids) delete next[id];
      return next;
    });
    setExpandedIds(prev => {
      const n = new Set(prev);
      for (const id of ids) n.delete(id);
      return n;
    });
    clearSelection();
    setBulkDeleteOpen(false);
    refreshProductOptions();
  };

  // Enqueue generation for a SAVED row. The backend either queues immediately
  // (templates ready) or marks the row auto_generate_when_ready and kicks off
  // the pipeline; the extraction-completed listener then enqueues generation.
  // Change the model on a not-yet-generated script; the next Generate uses it.
  const handleModelChange = async (storyboardId: string, model: string) => {
    setRows(prev => prev.map(r => r.storyboardId === storyboardId ? { ...r, generationModel: model } : r));
    await service.updateStoryboardModel(storyboardId, model);
  };

  const handleGenerate = async (storyboardId: string) => {
    setGeneratingId(storyboardId);
    const result = await service.generateSavedStoryboard(storyboardId);
    setGeneratingId(null);
    if (result?.adsNeedingPipeline && result.adsNeedingPipeline.length > 0) {
      const n = result.adsNeedingPipeline.length;
      setPipelineNoticeMsg(`Pipeline first: ${n} ad${n === 1 ? '' : 's'} need analysis. They'll process automatically; the script will generate when they're ready.`);
    }
    setDetailCache(prev => { const n = { ...prev }; delete n[storyboardId]; return n; });
    load(page, productFilter?.productId, { templateAdId });
  };

  // Open the creator-brief dialog for a finished script: load the detail (cache
  // first) for the script text, then fetch each reference ad's transcript +
  // base64 keyframe images concurrently so the dialog can preview + download.
  // Fetch a script's full detail + reference ads (transcripts + keyframe images),
  // shaped for the PDF builder. Shared by the brief-download and send-to-creator
  // dialogs so the fetch logic lives in one place.
  const loadScriptBundle = async (storyboardId: string): Promise<{
    storyboard: Storyboard | null; refs: ScriptPdfReference[]; script: string; productName: string | null;
  }> => {
    const row = rows.find(r => r.storyboardId === storyboardId);
    const productName = row ? (row.productTitle || `Product #${row.productId}`) : null;
    const detail = detailCache[storyboardId] || (await service.getStoryboard(storyboardId))?.storyboard || null;
    if (!detail) return { storyboard: null, refs: [], script: '', productName };
    const script =
      (detail.flatScript && detail.flatScript.trim())
      || (detail.segments || []).filter(s => s.scriptText && s.scriptText.trim()).map(s => s.scriptText.trim()).join('\n\n')
      || '';
    const refIds = Array.isArray(detail.referenceAdIds) ? detail.referenceAdIds : [];
    const refs = await Promise.all(refIds.map(async adId => {
      const ad = await service.getAd(adId);
      if (!ad) return null;
      let transcript: string | null = null;
      if (ad.transcriptUrl) {
        try { transcript = await (await fetch(ad.transcriptUrl)).text(); } catch { /* leave null */ }
      }
      // The bucket now allows cross-origin GET, so fetch each signed keyframe URL
      // and re-encode to PNG (robust for jsPDF) for embedding in the PDF.
      const kfResolved = await Promise.all((ad.keyframes || []).map(async (k): Promise<ScriptPdfKeyframe | null> => {
        const img = await fetchImageForPdf(k.url);
        return img ? { shotOrder: k.shotOrder, startTime: k.startTime, dataUrl: img.dataUrl, width: img.width, height: img.height } : null;
      }));
      const keyframes = kfResolved.filter((k): k is ScriptPdfKeyframe => k !== null);
      return { ad, transcript, keyframes } as ScriptPdfReference;
    }));
    return { storyboard: detail, refs: refs.filter((r): r is ScriptPdfReference => r !== null), script, productName };
  };

  const openBriefDialog = async (storyboardId: string) => {
    setBriefOpen(true);
    setBriefLoading(true);
    setBriefStoryboard(null);
    setBriefRefs([]);
    setBriefScript('');
    const { storyboard, refs, script, productName } = await loadScriptBundle(storyboardId);
    setBriefProductName(productName);
    setBriefStoryboard(storyboard);
    setBriefScript(script);
    setBriefRefs(refs);
    setBriefLoading(false);
  };

  const openSendDialog = async (storyboardId: string) => {
    setSendStoryboardId(storyboardId);
    setSendOpen(true);
    setSendLoading(true);
    setSendStoryboard(null);
    setSendRefs([]);
    setSendScript('');
    const { storyboard, refs, script, productName } = await loadScriptBundle(storyboardId);
    setSendProductName(productName);
    setSendStoryboard(storyboard);
    setSendScript(script);
    setSendRefs(refs);
    setSendLoading(false);
  };

  // Open the Generate dialog for a SAVED/FAILED row: prefill the model from the
  // row, then load the script detail (cache first) to prefill the prompt.
  const openGenerateDialog = async (row: StoryboardHistoryRow) => {
    setGenStoryboardId(row.storyboardId);
    setGenIsRetry(row.status === 'FAILED');
    setGenError(null);
    const model = coerceModelOption(row.generationModel);
    setGenModel(model);
    setGenOrigModel(model);
    setGenPrompt('');
    setGenOpen(true);
    setGenLoading(true);
    const detail = detailCache[row.storyboardId] || (await service.getStoryboard(row.storyboardId))?.storyboard || null;
    setGenPrompt(detail?.promptText || '');
    if (detail?.generationModel) {
      setGenModel(coerceModelOption(detail.generationModel));
      setGenOrigModel(coerceModelOption(detail.generationModel));
    }
    setGenLoading(false);
  };

  // Apply only the changed fields (model / prompt) to the existing row, then
  // enqueue generation via handleGenerate (which handles the pipeline notice +
  // list refresh). Fails loudly if a PATCH doesn't take.
  const submitGenerateDialog = async () => {
    if (!genStoryboardId) return;
    setGenBusy(true);
    setGenError(null);
    if (genModel !== genOrigModel) {
      const m = await service.updateStoryboardModel(genStoryboardId, genModel);
      if (!m) { setGenError('Failed to update the model. Please try again.'); setGenBusy(false); return; }
    }
    // Persist the prompt shown in the dialog (prompt_edited=TRUE) so generation
    // re-uses exactly this prompt rather than re-assembling a fresh one.
    const p = await service.updateStoryboardPrompt(genStoryboardId, genPrompt);
    if (!p?.promptEdited) {
      // The usual cause is a stale row: the backend refuses prompt edits
      // unless the script is SAVED or FAILED. Fetch the live status so the
      // message says what's actually happening.
      const fresh = (await service.getStoryboard(genStoryboardId))?.storyboard;
      if (fresh && fresh.status !== 'SAVED' && fresh.status !== 'FAILED') {
        setGenError(fresh.status === 'GENERATING'
          ? 'This script is already generating — close this dialog and watch its row.'
          : `This script is now ${fresh.status} — close this dialog; prompt edits only apply before generation.`);
        load(page, productFilter?.productId, { silent: true, templateAdId });
      } else {
        setGenError('Failed to save the prompt. Please try again.');
      }
      setGenBusy(false);
      return;
    }
    const id = genStoryboardId;
    setGenBusy(false);
    setGenOpen(false);
    await handleGenerate(id);
  };

  const filteredRows = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    return rows.filter(r => {
      if (statusFilter && r.status !== statusFilter) return false;
      if (!q) return true;
      return (
        (r.productTitle || '').toLowerCase().includes(q) ||
        (r.productStore || r.store || '').toLowerCase().includes(q) ||
        (r.generationModel || '').toLowerCase().includes(q) ||
        r.storyboardId.toLowerCase().includes(q) ||
        (r.referenceAdIds || []).some(id => id.toLowerCase().includes(q))
      );
    });
  }, [rows, filterText, statusFilter]);

  // Sortable table. Default to createdAt desc (newest first), matching
  // the SQL order. The useSortableTable hook coerces undefined values
  // to a stable end-of-list position.
  const { sorted: sortedRows, sortField, sortDir, handleSort } = useSortableTable<StoryboardHistoryRow>(
    filteredRows,
    'createdAt' as never,
    'desc',
  );

  // Refetch a storyboard's detail into the cache (after a segment edit /
  // regenerate mutates it server-side).
  const reloadDetail = useCallback(async (storyboardId: string) => {
    const result = await service.getStoryboard(storyboardId);
    if (result?.storyboard) {
      setDetailCache(prev => ({ ...prev, [storyboardId]: result.storyboard }));
    }
  }, []);

  // Lazy-load the heavy detail (prompt + segments) when a row expands.
  const handleExpand = async (storyboardId: string) => {
    if (expandedIds.has(storyboardId)) {
      setExpandedIds(prev => { const n = new Set(prev); n.delete(storyboardId); return n; });
      return;
    }
    setExpandedIds(prev => new Set(prev).add(storyboardId));
    if (detailCache[storyboardId]) return; // already cached
    setDetailLoading(prev => ({ ...prev, [storyboardId]: true }));
    setDetailError(prev => { const n = { ...prev }; delete n[storyboardId]; return n; });
    try {
      const result = await service.getStoryboard(storyboardId);
      if (result?.storyboard) {
        setDetailCache(prev => ({ ...prev, [storyboardId]: result.storyboard }));
      } else {
        setDetailError(prev => ({ ...prev, [storyboardId]: 'Storyboard not found.' }));
      }
    } catch (e) {
      setDetailError(prev => ({ ...prev, [storyboardId]: e instanceof Error ? e.message : String(e) }));
    } finally {
      setDetailLoading(prev => ({ ...prev, [storyboardId]: false }));
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h6">Scripts</Typography>
        {pollingPaused && hasActiveRows && (
          <Chip size="small" variant="outlined" color="warning"
            label="Live updates paused — move the mouse to resume"
            sx={{ height: 20, fontSize: '0.65rem' }} />
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every script request from the Script Builder — saved requests waiting to be generated, in-progress runs, and finished scripts. Expand a row to see the exact prompt sent to the LLM and, once generated, the structured storyboard and read-through script.
      </Typography>

      {templateAdId && (
        <Box sx={{ mb: 1.5 }}>
          <Chip
            label={`Template ad: ${templateAdId.slice(0, 8)}…`}
            color="primary"
            variant="outlined"
            onDelete={clearTemplateFilter}
            sx={{ fontFamily: 'ui-monospace, monospace' }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            Showing only scripts generated from this ad. Clear to see everything.
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Autocomplete<StoryboardProductSummary>
          size="small" sx={{ minWidth: 280 }}
          options={productOptions}
          value={productFilter}
          onChange={(_, next) => handleProductFilterChange(next)}
          getOptionLabel={p => p.productTitle || `Product #${p.productId}`}
          isOptionEqualToValue={(a, b) => a.productId === b.productId}
          renderOption={(props, p) => (
            <Box component="li" {...props} key={p.productId}
              sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                {p.productTitle || `Product #${p.productId}`}
              </Typography>
              <Chip size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }}
                label={`${p.storyboardCount}`} />
            </Box>
          )}
          renderInput={params => (
            <TextField {...params} label="Filter by product" placeholder="All products" />
          )}
        />
        <TextField size="small" placeholder="Filter by model / ad id…"
          value={filterText} onChange={e => setFilterText(e.target.value)}
          sx={{ minWidth: 240 }} />
        <Autocomplete
          multiple
          size="small"
          options={tagOptions}
          value={tagFilter}
          onChange={(_, next) => handleTagFilterChange(next)}
          sx={{ minWidth: 220 }}
          renderInput={params => <TextField {...params} label="Tags" placeholder="Filter by tag" />}
        />
        <TextField select size="small" label="Status"
          value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          sx={{ minWidth: 150 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="SAVED">Saved</MenuItem>
          <MenuItem value="GENERATING">Generating</MenuItem>
          <MenuItem value="DONE">Generated</MenuItem>
          <MenuItem value="FAILED">Failed</MenuItem>
        </TextField>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Refresh the list (the table is normally cached across tab switches)" arrow>
          <span>
            <IconButton size="small" disabled={loading}
              onClick={() => load(page, productFilter?.productId, { templateAdId })}>
              {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="caption" color="text.secondary">
          {total > 0 ? `${total} total · page ${page + 1} of ${totalPages}` : 'No storyboards yet.'}
        </Typography>
        <Button size="small" disabled={loading || page === 0}
          onClick={() => handlePageChange(Math.max(0, page - 1))}>
          ← Prev
        </Button>
        <Button size="small" disabled={loading || page >= totalPages - 1}
          onClick={() => handlePageChange(Math.min(totalPages - 1, page + 1))}>
          Next →
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {selectedCount > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: 1, bgcolor: 'action.selected', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {selectedCount} selected
          </Typography>
          <Button size="small" variant="contained" disabled={!!bulkBusy}
            onClick={handleBulkGenerate}>
            {bulkBusy === 'generate'
              ? <><CircularProgress size={14} sx={{ mr: 1 }} />Generating…</>
              : 'Generate'}
          </Button>
          <Button size="small" variant="outlined" color="warning" disabled={!!bulkBusy}
            onClick={() => setBulkDeleteOpen(true)}>
            Delete
          </Button>
          <Button size="small" disabled={!!bulkBusy} onClick={clearSelection}>
            Clear
          </Button>
        </Box>
      )}

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox size="small"
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected && !allVisibleSelected}
                    onChange={toggleAllVisible}
                    inputProps={{ 'aria-label': 'select all rows' }} />
                </TableCell>
                <TableCell width={36}></TableCell>
                <TableCell>
                  <TableSortLabel active={sortField === 'productTitle'} direction={sortField === 'productTitle' ? sortDir : 'asc'}
                    onClick={() => handleSort('productTitle' as never)}>
                    Product
                  </TableSortLabel>
                </TableCell>
                <TableCell>Base ads</TableCell>
                <TableCell>
                  <TableSortLabel active={sortField === 'generationModel'} direction={sortField === 'generationModel' ? sortDir : 'asc'}
                    onClick={() => handleSort('generationModel' as never)}>
                    Model
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortField === 'totalDurationSec'} direction={sortField === 'totalDurationSec' ? sortDir : 'asc'}
                    onClick={() => handleSort('totalDurationSec' as never)}>
                    Duration
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortField === 'createdAt'} direction={sortField === 'createdAt' ? sortDir : 'asc'}
                    onClick={() => handleSort('createdAt' as never)}>
                    Created
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortField === 'status'} direction={sortField === 'status' ? sortDir : 'asc'}
                    onClick={() => handleSort('status' as never)}>
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell width={48}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                      <CircularProgress size={18} />
                      <Typography variant="caption" color="text.secondary">Loading scripts…</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {!loading && filteredRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Typography variant="caption" color="text.secondary">
                      {filterText || statusFilter ? 'No rows match your filter.' : (
                        <>
                          No scripts yet.{' '}
                          <Link component="button" variant="caption"
                            onClick={() => history.push('/ad-library/product-mapping')}>
                            Create one in the Script Builder
                          </Link>
                        </>
                      )}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {sortedRows.map(row => {
                const isExpanded = expandedIds.has(row.storyboardId);
                return (
                  <React.Fragment key={row.storyboardId}>
                    <TableRow hover sx={{ cursor: 'pointer' }}
                      selected={selectedIds.has(row.storyboardId)}
                      onClick={() => handleExpand(row.storyboardId)}>
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox size="small"
                          checked={selectedIds.has(row.storyboardId)}
                          onChange={() => toggleRow(row.storyboardId)}
                          inputProps={{ 'aria-label': `select ${row.storyboardId}` }} />
                      </TableCell>
                      <TableCell onClick={(e) => { e.stopPropagation(); handleExpand(row.storyboardId); }}>
                        <IconButton size="small" aria-label={isExpanded ? 'Collapse row' : 'Expand row'}>
                          {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {row.productTitle || `Product #${row.productId}`}
                          </Typography>
                          {(row.productStore || row.store) && (
                            <Chip size="small" variant="outlined" sx={{ mt: 0.25, height: 18, fontSize: '0.65rem' }}
                              label={row.productStore || row.store} />
                          )}
                          {row.parentStoryboardId && (
                            <Tooltip title={`Variant of ${row.parentStoryboardId}`} arrow>
                              <Chip size="small" variant="outlined" color="secondary"
                                label="variant"
                                sx={{ mt: 0.25, ml: 0.5, height: 18, fontSize: '0.65rem' }} />
                            </Tooltip>
                          )}
                          {(row.tags || []).length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25, mt: 0.5 }}>
                              {(row.tags || []).map(t => (
                                <Chip key={t} size="small" color="primary" variant="outlined"
                                  label={t} sx={{ height: 18, fontSize: '0.65rem' }} />
                              ))}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(row.referenceAdIds || []).slice(0, 3).map(adId => (
                            <Chip key={adId} size="small" variant="outlined"
                              label={adId.slice(0, 8)}
                              onClick={() => setSelectedAdId(adId)}
                              sx={{ cursor: 'pointer' }} />
                          ))}
                          {(row.referenceAdIds || []).length > 3 && (
                            <Chip size="small" variant="outlined"
                              label={`+${(row.referenceAdIds || []).length - 3}`} />
                          )}
                          {(row.referenceAdIds || []).length === 0 && (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {(row.status === 'SAVED' || row.status === 'FAILED') ? (
                          <FormControl size="small" variant="standard" sx={{ minWidth: 150 }}>
                            <Select
                              value={coerceModelOption(row.generationModel)}
                              disabled={generatingId === row.storyboardId || !!row.autoGenerateWhenReady}
                              onChange={(e) => handleModelChange(row.storyboardId, e.target.value)}
                            >
                              {MODEL_OPTIONS.map(m => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Typography variant="caption" sx={{ fontFamily: 'ui-monospace, monospace' }}>
                            {row.generationModel || '—'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.totalDurationSec != null ? `${row.totalDurationSec}s` : '—'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{formatDateShort(row.createdAt)}</Typography>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const waiting = row.status === 'SAVED' && !!row.autoGenerateWhenReady;
                          const refStatuses = (row.referenceAdIds || [])
                            .map(id => refAdStatus[id])
                            .filter(Boolean) as RefStatus[];
                          const stage = deriveRefPipelineStage(row, refStatuses);
                          const isActive = row.status === 'GENERATING' || waiting;
                          // A DONE script that's been sent shows "Delivered (N)" (N = times sent).
                          const delivered = (row.deliveryCount || 0) > 0;
                          // FAILED rows surface the backend's error on hover.
                          const failedTooltip = row.status === 'FAILED' && row.errorMessage ? row.errorMessage : '';
                          return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
                              <Tooltip title={failedTooltip} arrow>
                                <Chip size="small"
                                  color={waiting ? 'warning' : delivered ? 'success' : statusColor(row.status)}
                                  variant={delivered ? 'filled' : 'outlined'}
                                  label={waiting ? 'Waiting for pipeline' : delivered ? `Delivered (${row.deliveryCount})` : statusLabel(row.status)}
                                  icon={isActive ? <CircularProgress size={10} color="inherit" /> : undefined}
                                  clickable={delivered}
                                  onClick={delivered ? (e) => {
                                    e.stopPropagation();
                                    setDeliveriesStoryboardId(row.storyboardId);
                                    setDeliveriesOpen(true);
                                  } : undefined}
                                  title={delivered ? 'View deliveries' : undefined}
                                  sx={{ height: 20, fontSize: '0.7rem' }} />
                              </Tooltip>
                              {stage && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}>
                                  {stage}
                                </Typography>
                              )}
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', gap: 0.25 }}>
                          {(row.status === 'SAVED' || row.status === 'FAILED') && (
                            <Tooltip title={
                              row.autoGenerateWhenReady ? 'Pipeline is running — generation will start when templates are ready'
                              : row.status === 'FAILED' ? 'Retry generation'
                              : 'Generate script'
                            }>
                              <span>
                                <IconButton size="small" color="primary"
                                  disabled={generatingId === row.storyboardId || !!row.autoGenerateWhenReady}
                                  onClick={() => openGenerateDialog(row)}>
                                  {generatingId === row.storyboardId
                                    ? <CircularProgress size={16} />
                                    : <PlayArrowIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {row.status === 'DONE' && (
                            <Tooltip title="Download creator brief (PDF)">
                              <IconButton size="small" color="primary"
                                onClick={() => openBriefDialog(row.storyboardId)}>
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {row.status === 'DONE' && (
                            <Tooltip title="Send to creator">
                              <IconButton size="small" color="primary"
                                onClick={() => openSendDialog(row.storyboardId)}>
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete storyboard">
                            <IconButton size="small" color="warning"
                              onClick={() => setDeleteTarget(row)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={10} sx={{ p: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <RowDetail
                            storyboardId={row.storyboardId}
                            productName={row.productTitle || `Product #${row.productId}`}
                            detail={detailCache[row.storyboardId] || null}
                            loading={!!detailLoading[row.storyboardId]}
                            error={detailError[row.storyboardId] || null}
                            errorMessage={row.errorMessage ?? null}
                            onReloadDetail={() => reloadDetail(row.storyboardId)}
                            onDuplicated={(newId) => {
                              setPipelineNoticeMsg(`Variant saved (${newId.slice(0, 8)}…) — generate it from the top of the list.`);
                              setPage(0);
                              load(0, productFilter?.productId, { templateAdId });
                            }}
                            onPromptSaved={(sbId, newPrompt) => {
                              setDetailCache(prev => {
                                const cur = prev[sbId];
                                if (!cur) return prev;
                                return { ...prev, [sbId]: { ...cur, promptText: newPrompt, promptEdited: true } };
                              });
                            }}
                            onFlatScriptSaved={(sbId, newScript) => {
                              setDetailCache(prev => {
                                const cur = prev[sbId];
                                if (!cur) return prev;
                                // Capture the original once (mirror the backend COALESCE) so
                                // "View original" works without a refetch.
                                return { ...prev, [sbId]: {
                                  ...cur,
                                  flatScript: newScript,
                                  originalFlatScript: cur.originalFlatScript ?? cur.flatScript ?? null,
                                  flatScriptEditedAt: new Date().toISOString(),
                                } };
                              });
                            }}
                            onRegenerated={(newSbId) => {
                              // Reload page 0 so the brand-new row appears
                              // at the top (sorted newest-first). The user
                              // can then watch it transition SAVED →
                              // GENERATING → DONE via the polling effect.
                              setPipelineNoticeMsg(`New script queued (${newSbId.slice(0, 8)}…). Watch the top of the list.`);
                              setPage(0);
                              load(0, productFilter?.productId, { templateAdId });
                            }}
                            tags={row.tags || []}
                            tagSuggestions={tagOptions}
                            onAddTag={(tag) => handleAddTag(row.storyboardId, tag)}
                            onRemoveTag={(tag) => handleRemoveTag(row.storyboardId, tag)}
                            onAddComment={(body) => handleAddComment(row.storyboardId, body)}
                            onUpdateComment={(commentId, body) => handleUpdateComment(row.storyboardId, commentId, body)}
                            onDeleteComment={(commentId) => handleDeleteComment(row.storyboardId, commentId)}
                            onDownloadBrief={openBriefDialog}
                          />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <AdDetailDialog adId={selectedAdId} onClose={() => setSelectedAdId(null)} />

      {/* Delete-confirm dialog. Cleanly removes the storyboard + every
          one of its segments from Snowflake on confirm. */}
      <Dialog open={deleteTarget != null} onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs" fullWidth>
        <DialogTitle>Delete this storyboard?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This will permanently remove the storyboard and every one of its segments.
          </Typography>
          {deleteTarget && (
            <Box sx={{ p: 1, bgcolor: '#fafafa', borderRadius: 1 }}>
              <Typography variant="caption" fontWeight={600} sx={{ display: 'block' }}>
                {deleteTarget.productTitle || `Product #${deleteTarget.productId}`}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'ui-monospace, monospace' }}>
                {deleteTarget.storyboardId}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Created {formatDateShort(deleteTarget.createdAt)}
              </Typography>
            </Box>
          )}
          {deleteError && <Alert severity="error" sx={{ mt: 1 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="warning" disabled={deleting}
            onClick={handleDeleteConfirm}>
            {deleting ? <><CircularProgress size={14} sx={{ mr: 1 }} />Deleting…</> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk-delete confirmation. Mirrors the single-row dialog but operates
          on the currently-selected set. */}
      <Dialog open={bulkDeleteOpen} onClose={() => bulkBusy !== 'delete' && setBulkDeleteOpen(false)}
        maxWidth="xs" fullWidth>
        <DialogTitle>Delete {selectedCount} storyboard{selectedCount === 1 ? '' : 's'}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will permanently remove {selectedCount} storyboard{selectedCount === 1 ? '' : 's'} and every one of their segments. This cannot be undone.
          </Typography>
          {bulkError && <Alert severity="error" sx={{ mt: 1 }}>{bulkError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteOpen(false)} disabled={bulkBusy === 'delete'}>Cancel</Button>
          <Button variant="contained" color="warning" disabled={bulkBusy === 'delete'}
            onClick={handleBulkDeleteConfirm}>
            {bulkBusy === 'delete' ? <><CircularProgress size={14} sx={{ mr: 1 }} />Deleting…</> : `Delete ${selectedCount}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate quick action — runs an existing SAVED/FAILED script, letting
          the user edit the prompt + pick the model first. Same shared modal as
          the Re-generate flow. */}
      <PromptModelDialog
        open={genOpen}
        onClose={() => setGenOpen(false)}
        title={genIsRetry ? 'Retry generation' : 'Generate script'}
        description="Review the prompt and model below, then generate. Edits are saved to this script before it runs."
        model={genModel}
        onModelChange={setGenModel}
        promptText={genPrompt}
        onPromptChange={setGenPrompt}
        loading={genLoading}
        busy={genBusy}
        error={genError}
        confirmLabel={genIsRetry ? 'Retry generation' : 'Generate script'}
        onConfirm={submitGenerateDialog}
      />

      <ScriptBriefDialog
        open={briefOpen}
        onClose={() => setBriefOpen(false)}
        loading={briefLoading}
        storyboard={briefStoryboard}
        productName={briefProductName}
        references={briefRefs}
        initialScript={briefScript}
      />

      <SendToCreatorDialog
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        loading={sendLoading}
        storyboardId={sendStoryboardId}
        storyboard={sendStoryboard}
        productName={sendProductName}
        references={sendRefs}
        initialScript={sendScript}
        onSent={() => load(page, productFilter?.productId, { silent: true, templateAdId })}
      />

      <DeliveriesDialog
        open={deliveriesOpen}
        onClose={() => setDeliveriesOpen(false)}
        storyboardId={deliveriesStoryboardId}
      />

      <Snackbar
        open={!!pipelineNoticeMsg}
        autoHideDuration={6000}
        onClose={() => setPipelineNoticeMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={pipelineNoticeMsg || ''}
      />
    </Box>
  );
}

// ── Inline row-detail panel ──

interface RowDetailProps {
  storyboardId: string;
  productName: string;
  detail: Storyboard | null;
  loading: boolean;
  error: string | null;
  // Generation-failure reason from the history row (FAILED rows only).
  errorMessage?: string | null;
  onPromptSaved?: (storyboardId: string, newPrompt: string) => void;
  onFlatScriptSaved?: (storyboardId: string, newScript: string) => void;
  // Fires after a Regenerate flow completes (new SAVED row created +
  // enqueued for generation). The parent reloads the storyboard list so
  // the new row appears immediately at the top.
  onRegenerated?: (newStoryboardId: string) => void;
  // Refetch this storyboard's detail into the parent cache (after a
  // segment edit / regenerate).
  onReloadDetail?: () => Promise<void> | void;
  // Fires after "Duplicate as variant" saves the new SAVED row.
  onDuplicated?: (newStoryboardId: string) => void;
  tags: string[];
  tagSuggestions: string[];
  onAddTag: (tag: string) => Promise<void> | void;
  onRemoveTag: (tag: string) => Promise<void> | void;
  onAddComment: (body: string) => Promise<void>;
  onUpdateComment: (commentId: string, body: string) => Promise<void>;
  onDeleteComment: (commentId: string) => Promise<void>;
  onDownloadBrief: (storyboardId: string) => void;
}

function RowDetail({ storyboardId, productName, detail, loading, error, errorMessage, onPromptSaved, onFlatScriptSaved, onRegenerated, onReloadDetail, onDuplicated, tags, tagSuggestions, onAddTag, onRemoveTag, onAddComment, onUpdateComment, onDeleteComment, onDownloadBrief }: RowDetailProps) {
  const [promptOpen, setPromptOpen] = useState(false);
  const [storyboardOpen, setStoryboardOpen] = useState(false);
  const [scriptOpen, setScriptOpen] = useState(true);
  // Comments default open when the script already has notes, so they're not
  // hidden; collapsed otherwise to keep the detail compact.
  const comments = detail?.comments || [];
  const [commentsOpen, setCommentsOpen] = useState(comments.length > 0);

  // Prompt-edit state. Only meaningful while detail.status === 'SAVED'.
  // Worker treats the row as "use stored prompt verbatim" once prompt_edited
  // is TRUE, so a save here is the user's commitment to that exact text.
  const [editingPrompt, setEditingPrompt] = useState(false);
  const [editedPromptText, setEditedPromptText] = useState('');
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [promptSaveError, setPromptSaveError] = useState<string | null>(null);

  // Read-through (flat_script) edit state. Edits persist and override the
  // generated script; the original generated text stays available to view.
  const [editingScript, setEditingScript] = useState(false);
  const [editedScriptText, setEditedScriptText] = useState('');
  const [savingScript, setSavingScript] = useState(false);
  const [scriptSaveError, setScriptSaveError] = useState<string | null>(null);
  // Outcome of pushing an edited script down into the segments' clip lines —
  // either how many segments changed, or why the sync was refused.
  const [scriptSyncNote, setScriptSyncNote] = useState<string | null>(null);
  const [showOriginalScript, setShowOriginalScript] = useState(false);

  // Regenerate state. Opens a dialog with the existing prompt prefilled so
  // the user can tweak it and produce a NEW storyboard (new row, same
  // briefSnapshot, prompt_edited=TRUE) without disturbing the original.
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenPromptText, setRegenPromptText] = useState('');
  const [regenModel, setRegenModel] = useState<string>(DEFAULT_GENERATION_MODEL);
  const [regenBusy, setRegenBusy] = useState(false);
  const [regenError, setRegenError] = useState<string | null>(null);

  // "Duplicate as variant" — clones the config (with parentStoryboardId)
  // into a new SAVED row, without generating.
  const [duplicating, setDuplicating] = useState(false);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);

  // Per-segment inline edit. Only the clip DO actions are editable — the
  // spoken words come from the read-through script (saving it re-flows them
  // into the clips), so there's no per-segment script/visual/text editing and
  // no per-segment regenerate.
  const [editingSegId, setEditingSegId] = useState<string | null>(null);
  const [segDraft, setSegDraft] = useState<{ shots: StoryboardSegmentShot[]; clips: Array<{ clipName: string; actionsText: string }> }>({ shots: [], clips: [] });
  const [segBusy, setSegBusy] = useState(false);
  const [segError, setSegError] = useState<string | null>(null);

  // Confirmation toast for the per-hook-option Copy buttons.
  const [copyToastMsg, setCopyToastMsg] = useState<string | null>(null);
  const [hooksOpen, setHooksOpen] = useState(true);

  const startSegmentEdit = (seg: StoryboardSegment) => {
    const shots = seg.shots || [];
    const clips = groupShotsIntoClips(shots);
    setEditingSegId(seg.segmentId);
    setSegDraft({
      shots,
      // One textarea per clip, its actions one per line. Old storyboards
      // without clip names fall back to a single list over all cuts.
      clips: clips
        ? clips.map(c => ({ clipName: c.clipName, actionsText: c.actions.join('\n') }))
        : [],
    });
    setSegError(null);
  };

  const saveSegmentEdit = async () => {
    if (!editingSegId) return;
    setSegBusy(true);
    setSegError(null);
    const edits: Record<string, string[]> = {};
    for (const clip of segDraft.clips) {
      edits[clip.clipName] = clip.actionsText.split('\n').map(l => l.trim()).filter(Boolean);
    }
    const shots = applyClipActions(segDraft.shots, edits);
    const result = await service.updateStoryboardSegment(storyboardId, editingSegId, { shots });
    setSegBusy(false);
    if (result?.segment) {
      setEditingSegId(null);
      await onReloadDetail?.();
    } else {
      setSegError('Failed to save the actions. Please try again.');
    }
  };

  // Lazy-loaded reference-ad data keyed by adId, used for the side-by-side
  // "Original reference" pane next to the generated script + the keyframe
  // strip under it. Cached at the RowDetail level so re-collapsing/re-
  // expanding the section doesn't refetch.
  const [refTranscripts, setRefTranscripts] = useState<Record<string, string>>({});
  const [refMetadata, setRefMetadata] = useState<Record<string, { pageName: string | null; headline: string | null; videoUrl: string | null; archivedVideoShareUrl: string | null }>>({});
  const [refKeyframes, setRefKeyframes] = useState<Record<string, Keyframe[]>>({});
  const [refLoading, setRefLoading] = useState(false);

  // Seed keyframes from localStorage immediately so the strip paints
  // before the network round-trip resolves. The background fetch below
  // refreshes signed URLs (which expire on the order of minutes) — until
  // then the cached URLs may 4xx, which is acceptable: the in-flight
  // refresh swaps them out within a second or so.
  useEffect(() => {
    if (!detail) return;
    const adIds = Array.isArray(detail.referenceAdIds) ? detail.referenceAdIds : [];
    if (adIds.length === 0) return;
    const seeded: Record<string, Keyframe[]> = {};
    for (const adId of adIds) {
      const cached = getCachedKeyframes(adId);
      if (cached && cached.length > 0) seeded[adId] = cached;
    }
    if (Object.keys(seeded).length > 0) setRefKeyframes(prev => ({ ...prev, ...seeded }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.storyboardId]);

  useEffect(() => {
    if (!detail) return;
    const adIds = Array.isArray(detail.referenceAdIds) ? detail.referenceAdIds : [];
    if (adIds.length === 0) return;
    let cancelled = false;
    setRefLoading(true);
    (async () => {
      // Fetch each reference ad in parallel, then pull the transcript text
      // and keyframes from the signed URLs on the CIAdDetail. The keyframes
      // come back already on the ad detail (server-side cached, so this is
      // fast); we just persist them into localStorage for next-time instant
      // paint.
      const results = await Promise.all(adIds.map(async adId => {
        const ad = await service.getAd(adId);
        if (!ad) return { adId, transcript: null, pageName: null, headline: null, videoUrl: null, archivedVideoShareUrl: null, keyframes: [] as Keyframe[] };
        let transcript: string | null = null;
        if (ad.transcriptUrl) {
          try {
            const res = await fetch(ad.transcriptUrl);
            transcript = await res.text();
          } catch (_) { /* leave null — UI shows a placeholder */ }
        }
        return {
          adId,
          transcript,
          pageName: ad.pageName,
          headline: ad.headline,
          videoUrl: ad.videoUrl,
          archivedVideoShareUrl: ad.archivedVideoShareUrl ?? null,
          keyframes: (ad.keyframes || []) as Keyframe[],
        };
      }));
      if (cancelled) return;
      const nextTranscripts: Record<string, string> = {};
      const nextMeta: Record<string, { pageName: string | null; headline: string | null; videoUrl: string | null; archivedVideoShareUrl: string | null }> = {};
      const nextKeyframes: Record<string, Keyframe[]> = {};
      for (const r of results) {
        if (r.transcript) nextTranscripts[r.adId] = r.transcript;
        nextMeta[r.adId] = { pageName: r.pageName, headline: r.headline, videoUrl: r.videoUrl, archivedVideoShareUrl: r.archivedVideoShareUrl };
        if (r.keyframes && r.keyframes.length > 0) {
          nextKeyframes[r.adId] = r.keyframes;
          setCachedKeyframes(r.adId, r.keyframes);
        }
      }
      setRefTranscripts(nextTranscripts);
      setRefMetadata(nextMeta);
      setRefKeyframes(prev => ({ ...prev, ...nextKeyframes }));
      setRefLoading(false);
    })();
    return () => { cancelled = true; };
  // We only need to re-fetch when the storyboard (and therefore its
  // reference set) changes — keying on storyboardId is enough.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.storyboardId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">Loading prompt + script…</Typography>
      </Box>
    );
  }
  if (error) return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  if (!detail) return null;

  const copy = (text: string) => navigator.clipboard.writeText(text);
  const isSaved = detail.status === 'SAVED';
  const refAdIds = Array.isArray(detail.referenceAdIds) ? detail.referenceAdIds : [];
  const hasAnyOriginal = refAdIds.some(adId => !!refTranscripts[adId]);

  return (
    // `contain: inline-size` is the load-bearing rule here. Without it the
    // table cell's natural width is measured from its descendants, which
    // means the keyframes strip's intrinsic content width propagates up
    // and stretches the cell → stretches the column → stretches the
    // table → triggers TableContainer's horizontal scrollbar. Setting
    // inline-size containment tells the browser to size descendants
    // assuming this element IS the containing block, so the strip's
    // overflowX: auto can do its job inside the cell.
    <Box sx={{
      p: 2,
      bgcolor: '#fafafa',
      maxWidth: '100%',
      minWidth: 0,
      overflow: 'hidden',
      contain: 'inline-size',
    }}>
      {detail.status === 'FAILED' && errorMessage && (
        <Alert severity="error" sx={{ mb: 1.5 }}>{errorMessage}</Alert>
      )}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Button size="small" variant="outlined" startIcon={<ContentCopyIcon fontSize="small" />}
          onClick={() => copy(detail.promptText || '(no prompt available)')}>
          Copy prompt
        </Button>
        {detail.status === 'DONE' && detail.briefSnapshot && (
          <Button size="small" variant="outlined" startIcon={<RefreshIcon fontSize="small" />}
            onClick={() => {
              setRegenPromptText(detail.promptText || '');
              setRegenModel(coerceModelOption(detail.generationModel));
              setRegenError(null);
              setRegenOpen(true);
            }}>
            Regenerate (Prompt Editable)
          </Button>
        )}
        {detail.briefSnapshot && (
          <Tooltip title="Save a copy of this request (same templates + config) as a new row you can tweak and generate separately">
            <span>
              <Button size="small" variant="outlined" disabled={duplicating}
                startIcon={duplicating ? <CircularProgress size={14} /> : <ContentCopyIcon fontSize="small" />}
                onClick={async () => {
                  if (!detail.briefSnapshot) return;
                  setDuplicating(true);
                  setDuplicateError(null);
                  const saved = await service.saveStoryboardRequest(
                    detail.productId,
                    { ...detail.briefSnapshot, parentStoryboardId: detail.storyboardId },
                  );
                  setDuplicating(false);
                  if (saved?.storyboardId) onDuplicated?.(saved.storyboardId);
                  else setDuplicateError('Failed to duplicate this script.');
                }}>
                Duplicate as variant
              </Button>
            </span>
          </Tooltip>
        )}
        {detail.status === 'DONE' && (
          <Button size="small" variant="outlined"
            startIcon={<DownloadIcon fontSize="small" />}
            onClick={() => onDownloadBrief(detail.storyboardId)}>
            Download brief (PDF)
          </Button>
        )}
        {!isSaved && (
          <>
            <Button size="small" variant="outlined" startIcon={<ContentCopyIcon fontSize="small" />}
              onClick={() => copy(formatScriptForLLM(detail, productName))}>
              Copy script (LLM)
            </Button>
            <Button size="small" variant="outlined" startIcon={<ContentCopyIcon fontSize="small" />}
              onClick={() => copy(formatStoryboardForLLM(detail, productName))}>
              Copy storyboard (LLM)
            </Button>
          </>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
          {detail.storyboardId}
        </Typography>
      </Box>

      {duplicateError && <Alert severity="error" sx={{ mb: 1.5 }}>{duplicateError}</Alert>}

      {/* Token usage. */}
      {(detail.tokensIn != null || detail.tokensOut != null) && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Tokens: {detail.tokensIn != null ? detail.tokensIn.toLocaleString() : '—'} in · {detail.tokensOut != null ? detail.tokensOut.toLocaleString() : '—'} out
          </Typography>
        </Box>
      )}

      <Box sx={{ mb: 1.5, maxWidth: 480 }}>
        <TagEditor
          tags={tags}
          suggestions={tagSuggestions}
          onAdd={onAddTag}
          onRemove={onRemoveTag}
        />
      </Box>

      <CollapsibleBlock
        title="Comments / Notes"
        sub={comments.length > 0 ? `${comments.length}` : undefined}
        open={commentsOpen}
        onToggle={() => setCommentsOpen(o => !o)}>
        <CommentsSection
          comments={comments}
          onAdd={onAddComment}
          onUpdate={onUpdateComment}
          onDelete={onDeleteComment}
        />
      </CollapsibleBlock>

      {/* Re-generate — clones the row's config into a new SAVED storyboard with
          the edited prompt and immediately enqueues it. Uses the same shared
          modal as the table's Generate quick action. */}
      <PromptModelDialog
        open={regenOpen}
        onClose={() => setRegenOpen(false)}
        title="Re-generate with edited prompt"
        description="A new script will be created using the same templates, product brief, and config as the original — only the prompt and model below will differ."
        model={regenModel}
        onModelChange={setRegenModel}
        promptText={regenPromptText}
        onPromptChange={setRegenPromptText}
        busy={regenBusy}
        error={regenError}
        confirmLabel="Generate new script"
        confirmDisabled={!detail.briefSnapshot}
        onConfirm={async () => {
          if (!detail.briefSnapshot) return;
          setRegenBusy(true);
          setRegenError(null);
          // 1. Save a new request reusing the original config, but with the
          //    chosen generation model — produces a SAVED row with the
          //    auto-assembled prompt. config.model is persisted to the row
          //    and read back at generation time.
          const saved = await service.saveStoryboardRequest(detail.productId, { ...detail.briefSnapshot, model: regenModel });
          if (!saved?.storyboardId) {
            setRegenBusy(false);
            setRegenError('Failed to save the new request.');
            return;
          }
          // 2. Overwrite the prompt with the user-edited text and mark
          //    prompt_edited=TRUE so the worker uses it verbatim.
          const patched = await service.updateStoryboardPrompt(saved.storyboardId, regenPromptText);
          if (!patched?.promptEdited) {
            setRegenBusy(false);
            setRegenError('Saved the new request, but failed to apply the edited prompt. You can edit it manually from the new row.');
            onRegenerated?.(saved.storyboardId);
            setRegenOpen(false);
            return;
          }
          // 3. Enqueue generation.
          await service.generateSavedStoryboard(saved.storyboardId);
          setRegenBusy(false);
          setRegenOpen(false);
          onRegenerated?.(saved.storyboardId);
        }}
      />

      <CollapsibleBlock title="Prompt" open={promptOpen} onToggle={() => setPromptOpen(o => !o)}
        sub={detail.promptText ? `${detail.promptText.length.toLocaleString()} chars${detail.promptEdited ? ' · edited' : ''}` : 'not stored'}>
        {editingPrompt && isSaved ? (
          <Box>
            <TextField
              value={editedPromptText}
              onChange={e => setEditedPromptText(e.target.value)}
              fullWidth multiline minRows={12} maxRows={20}
              variant="outlined"
              disabled={savingPrompt}
              InputProps={{ sx: { fontFamily: 'ui-monospace, monospace', fontSize: '0.78rem', bgcolor: '#fff' } }}
            />
            {promptSaveError && <Alert severity="error" sx={{ mt: 1 }}>{promptSaveError}</Alert>}
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button size="small" variant="contained"
                disabled={savingPrompt || !editedPromptText.trim() || editedPromptText === (detail.promptText || '')}
                onClick={async () => {
                  setSavingPrompt(true);
                  setPromptSaveError(null);
                  const result = await service.updateStoryboardPrompt(storyboardId, editedPromptText);
                  setSavingPrompt(false);
                  if (result?.promptEdited) {
                    setEditingPrompt(false);
                    onPromptSaved?.(storyboardId, editedPromptText);
                  } else {
                    // The backend returns 409 specifically when the row is
                    // no longer SAVED (e.g., the worker already picked it
                    // up). Any other failure (500, network) bubbles up
                    // here as null too — the message should distinguish.
                    setPromptSaveError('Save failed. The row may have already moved past SAVED (check status), or the server hit an error — try again.');
                  }
                }}>
                {savingPrompt ? <><CircularProgress size={14} sx={{ mr: 1 }} />Saving…</> : 'Save prompt'}
              </Button>
              <Button size="small" disabled={savingPrompt}
                onClick={() => { setEditingPrompt(false); setPromptSaveError(null); }}>
                Cancel
              </Button>
            </Box>
          </Box>
        ) : detail.promptText ? (
          <Box>
            {isSaved && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                <Button size="small" variant="outlined"
                  onClick={() => {
                    setEditedPromptText(detail.promptText || '');
                    setPromptSaveError(null);
                    setEditingPrompt(true);
                  }}>
                  Edit prompt
                </Button>
              </Box>
            )}
            <Box sx={{ p: 1.5, bgcolor: '#fff', borderRadius: 1,
              fontFamily: 'ui-monospace, monospace', fontSize: '0.78rem',
              whiteSpace: 'pre-wrap', maxHeight: 360, overflow: 'auto' }}>
              {detail.promptText}
            </Box>
          </Box>
        ) : (
          <Typography variant="caption" color="text.disabled">
            Prompt text wasn't snapshotted for this storyboard (predates the PROMPT_TEXT column).
          </Typography>
        )}
      </CollapsibleBlock>

      {isSaved && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 0.5, py: 1 }}>
          This request hasn't been generated yet — use the Generate (▶) button in the table row to produce the script.
        </Typography>
      )}
      {!isSaved && (<>
      {(detail.hookOptions || []).length > 0 && (
        <CollapsibleBlock title="Hook options" open={hooksOpen}
          onToggle={() => setHooksOpen(o => !o)}
          sub={`${(detail.hookOptions || []).length}`}>
          <Box>
            {(detail.hookOptions || []).map((opt, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1, p: 1, bgcolor: '#fafafa', borderRadius: 1, '&:last-child': { mb: 0 } }}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{opt.hookLine || '—'}</Typography>
                  {opt.onScreenText && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                      On-screen: {opt.onScreenText}
                    </Typography>
                  )}
                </Box>
                {opt.hookDevice && (
                  <Chip size="small" variant="outlined" label={formatEnumLabel(opt.hookDevice)}
                    sx={{ height: 20, fontSize: '0.65rem', flexShrink: 0 }} />
                )}
                <Button size="small" sx={{ flexShrink: 0 }}
                  startIcon={<ContentCopyIcon fontSize="small" />}
                  onClick={() => {
                    copy([opt.hookLine, opt.onScreenText ? `[TEXT: "${opt.onScreenText}"]` : null]
                      .filter(Boolean).join(' '));
                    setCopyToastMsg('Hook copied to clipboard.');
                  }}>
                  Copy
                </Button>
              </Box>
            ))}
          </Box>
        </CollapsibleBlock>
      )}
      <CollapsibleBlock title="Storyboard segments" open={storyboardOpen}
        onToggle={() => setStoryboardOpen(o => !o)}
        sub={`${(detail.segments || []).length} segment${(detail.segments || []).length === 1 ? '' : 's'}`}>
        <Box>
          {segError && <Alert severity="error" sx={{ mb: 1 }}>{segError}</Alert>}
          {(detail.segments || []).map(seg => (
            <Box key={seg.segmentId} sx={{ mb: 1, p: 1, bgcolor: '#fff', borderRadius: 1, borderLeft: '3px solid #757575' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography variant="caption" fontWeight={700}>{seg.segmentOrder}.</Typography>
                <Chip size="small" variant="outlined" label={formatEnumLabel(seg.segmentRole)}
                  sx={{ height: 18, fontSize: '0.65rem' }} />
                {seg.durationSec != null && (
                  <Chip size="small" variant="outlined" label={`${seg.durationSec}s`}
                    sx={{ height: 18, fontSize: '0.65rem' }} />
                )}
                {seg.pacing && <Chip size="small" variant="outlined" label={formatEnumLabel(seg.pacing)}
                  sx={{ height: 18, fontSize: '0.65rem' }} />}
                <Box sx={{ flexGrow: 1 }} />
                <Button size="small" sx={{ minWidth: 0, fontSize: '0.7rem' }}
                  disabled={segBusy}
                  onClick={() => (editingSegId === seg.segmentId ? setEditingSegId(null) : startSegmentEdit(seg))}>
                  {editingSegId === seg.segmentId ? 'Cancel' : 'Edit actions'}
                </Button>
              </Box>
              {editingSegId === seg.segmentId ? (
                <Box>
                  {/* Only the DO actions are editable here. The spoken words
                      come from the read-through script (editing it re-flows
                      them into these clips), and the on-screen title / visual
                      direction are carried by the clip lines themselves. */}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    One action per line — these are the moves the creator hits inside each clip.
                    To change the spoken words, edit the read-through script below.
                  </Typography>
                  {segDraft.clips.map((clip, ci) => (
                    <TextField key={clip.clipName} fullWidth multiline minRows={1} maxRows={6} size="small" sx={{ mb: 1 }}
                      label={`Do — ${clip.clipName}`} value={clip.actionsText} disabled={segBusy}
                      onChange={e => setSegDraft(d => ({
                        ...d,
                        clips: d.clips.map((c, i) => (i === ci ? { ...c, actionsText: e.target.value } : c)),
                      }))} />
                  ))}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="contained" disabled={segBusy} onClick={saveSegmentEdit}>
                      {segBusy ? <><CircularProgress size={12} sx={{ mr: 0.5 }} />Saving…</> : 'Save'}
                    </Button>
                    <Button size="small" disabled={segBusy} onClick={() => setEditingSegId(null)}>Cancel</Button>
                  </Box>
                </Box>
              ) : (<>
              {seg.scriptText && (
                <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                  <b>Script:</b> {seg.scriptText}
                </Typography>
              )}
              {/* The clip view below carries actions/framing/title, so the
                  segment-level summary lines only print for old storyboards
                  without clip names. */}
              {!groupShotsIntoClips(seg.shots) && (<>
                {seg.visualDirection && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap', mt: 0.25 }}>
                    <b>Visual:</b> {seg.visualDirection}
                  </Typography>
                )}
                {seg.shotType && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    <b>Shot:</b> {seg.shotType}
                  </Typography>
                )}
                {seg.onScreenText && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                    <b>On-screen:</b> {seg.onScreenText}
                  </Typography>
                )}
              </>)}
              {(seg.shots || []).length > 0 && (() => {
                const clips = groupShotsIntoClips(seg.shots);
                if (!clips) {
                  // Old storyboards without clip names: per-cut fallback.
                  return (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        <b>Cuts ({(seg.shots || []).length}):</b>
                      </Typography>
                      {(seg.shots || []).map((shot, i) => (
                        <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block', pl: 1.5 }}>
                          {i + 1}. {[
                            shot.shotRole ? formatShotRole(shot.shotRole) : null,
                            shot.durationSec != null ? `~${shot.durationSec}s` : null,
                            ...[shot.framing, shot.cameraMovement].filter(Boolean)
                              .map(v => formatEnumLabel(v as string)),
                          ].filter(Boolean).join(' · ')}
                          {shot.description ? ` — ${shot.description}` : ''}
                          {shot.line ? ` · Suggested script to read: "${shot.line}"` : ''}
                          {shot.onScreenText ? ` · Title on screen: "${shot.onScreenText}"` : ''}
                        </Typography>
                      ))}
                    </Box>
                  );
                }
                return (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      <b>Clips ({clips.length}):</b>
                    </Typography>
                    {clips.map((clip) => (
                      <Box key={clip.clipName} sx={{ pl: 1.5, mb: 0.75 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                          {clip.clipName} — {clipSpecs(clip).join(' · ')}
                        </Typography>
                        {clip.title && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 1.5 }}>
                            Title on screen: "{clip.title}"
                          </Typography>
                        )}
                        {clip.actions.length > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 1.5 }}>
                            Suggested take: {clip.actions.join(' → ')}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 1.5 }}>
                          Suggested script to read: {clip.suggested ? `"${clip.suggested}"` : '(film silent)'}
                        </Typography>
                        {clip.cutDurations.length > 1 && (
                          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', pl: 1.5 }}>
                            Rough edit plan: {clip.cutDurations.map(d => (d != null ? `${d}s` : '?')).join(' + ')} cuts
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                );
              })()}
              {seg.brollNotes && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                  <b>Extra B-Roll:</b> {seg.brollNotes}
                </Typography>
              )}
              </>)}
            </Box>
          ))}
        </Box>
      </CollapsibleBlock>

      <CollapsibleBlock title="Read-through script" open={scriptOpen}
        onToggle={() => setScriptOpen(o => !o)}
        sub={detail.flatScript ? `${detail.flatScript.length.toLocaleString()} chars` : 'empty'}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>Script</Typography>
              {detail.flatScriptEditedAt && (
                <Typography variant="caption" color="text.secondary">
                  · edited {new Date(detail.flatScriptEditedAt).toLocaleDateString()}
                </Typography>
              )}
              <Box sx={{ flexGrow: 1 }} />
              {detail.status === 'DONE' && !editingScript && (
                <Button size="small" onClick={() => { setEditedScriptText(detail.flatScript || ''); setScriptSaveError(null); setEditingScript(true); }}>
                  Edit
                </Button>
              )}
            </Box>
            {editingScript ? (
              <Box>
                <TextField
                  value={editedScriptText}
                  onChange={e => setEditedScriptText(e.target.value)}
                  fullWidth multiline minRows={12} maxRows={24} variant="outlined" disabled={savingScript}
                  InputProps={{ sx: { fontSize: '0.85rem', bgcolor: '#fff' } }}
                />
                {scriptSaveError && <Alert severity="error" sx={{ mt: 1 }}>{scriptSaveError}</Alert>}
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button size="small" variant="contained"
                    disabled={savingScript || !editedScriptText.trim() || editedScriptText === (detail.flatScript || '')}
                    onClick={async () => {
                      setSavingScript(true);
                      setScriptSaveError(null);
                      setScriptSyncNote(null);
                      const result = await service.updateStoryboardFlatScript(storyboardId, editedScriptText);
                      setSavingScript(false);
                      if (result?.flatScriptEdited) {
                        setEditingScript(false);
                        onFlatScriptSaved?.(storyboardId, editedScriptText);
                        // The save re-flows the edited wording into the
                        // segments' clip lines — reload so the storyboard
                        // cards (and any PDF built from them) show it.
                        if (result.segmentsSynced) {
                          setScriptSyncNote(result.updatedSegments
                            ? `Updated the suggested lines in ${result.updatedSegments} segment${result.updatedSegments === 1 ? '' : 's'}.`
                            : null);
                          await onReloadDetail?.();
                        } else if (result.syncReason) {
                          setScriptSyncNote(`Saved, but the clips still show the previous wording — ${result.syncReason}.`);
                        }
                      } else {
                        setScriptSaveError('Save failed. The script must be in a generated (DONE) state, or the server errored — try again.');
                      }
                    }}>
                    {savingScript ? <><CircularProgress size={14} sx={{ mr: 1 }} />Saving…</> : 'Save script'}
                  </Button>
                  <Button size="small" disabled={savingScript} onClick={() => { setEditingScript(false); setScriptSaveError(null); }}>Cancel</Button>
                </Box>
              </Box>
            ) : detail.flatScript ? (<>
              {scriptSyncNote && (
                <Alert severity={scriptSyncNote.startsWith('Saved, but') ? 'warning' : 'success'}
                  sx={{ mb: 1, py: 0 }} onClose={() => setScriptSyncNote(null)}>
                  {scriptSyncNote}
                </Alert>
              )}
              <Box sx={{ p: 1.5, bgcolor: '#fff', borderRadius: 1, whiteSpace: 'pre-wrap',
                fontFamily: 'inherit', fontSize: '0.85rem', maxHeight: 360, overflow: 'auto' }}>
                {detail.flatScript}
              </Box>
            </>) : (
              <Typography variant="caption" color="text.disabled">No script.</Typography>
            )}
            {!editingScript && detail.flatScriptEditedAt && detail.originalFlatScript && (
              <Box sx={{ mt: 0.5 }}>
                <Button size="small" sx={{ minWidth: 0, p: 0, textTransform: 'none' }}
                  onClick={() => setShowOriginalScript(o => !o)}>
                  {showOriginalScript ? 'Hide original' : 'View original (generated)'}
                </Button>
                {showOriginalScript && (
                  <Box sx={{ mt: 0.5, p: 1.5, bgcolor: '#f7f7f7', borderRadius: 1, whiteSpace: 'pre-wrap',
                    fontFamily: 'inherit', fontSize: '0.8rem', maxHeight: 300, overflow: 'auto', border: '1px solid', borderColor: 'divider' }}>
                    {detail.originalFlatScript}
                  </Box>
                )}
              </Box>
            )}
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
              Original reference{refLoading && !hasAnyOriginal ? ' (loading…)' : ''}
            </Typography>
            <Box sx={{ p: 1.5, bgcolor: '#fff', borderRadius: 1, maxHeight: 360, overflow: 'auto' }}>
              {refAdIds.length === 0 && (
                <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                  No reference ad on file.
                </Typography>
              )}
              {refAdIds.length > 0 && !hasAnyOriginal && (
                <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                  {refLoading ? 'Loading reference transcript…' : 'Reference transcript not available.'}
                </Typography>
              )}
              {refAdIds.map(adId => {
                const transcript = refTranscripts[adId];
                if (!transcript) return null;
                const meta = refMetadata[adId];
                return (
                  <Box key={adId} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
                    {refAdIds.length > 1 && (
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.25 }}>
                        From {meta?.pageName || adId.slice(0, 8)}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.85rem' }}>
                      {transcript}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        {/* Original reference ad media — one block per ref ad. Video player
            first (so the script can be heard alongside the read-through),
            then the keyframes strip below it. Thumbnails are sized to
            roughly match the detail-dialog look (~140px wide, 16:9). Strip
            scrolls horizontally; clicking a frame opens it full-size.
            Cached client-side via keyframeCache + server-side via the
            shots-data Map; refresh via the Ad Detail dialog's button. */}
        {refAdIds.some(adId => refMetadata[adId]?.videoUrl || refMetadata[adId]?.archivedVideoShareUrl || (refKeyframes[adId] || []).length > 0) && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.75 }}>
              Original reference media
            </Typography>
            {refAdIds.map(adId => {
              const frames = refKeyframes[adId] || [];
              const meta = refMetadata[adId];
              if (!meta?.videoUrl && !meta?.archivedVideoShareUrl && frames.length === 0) return null;
              return (
                <Box key={adId} sx={{ mb: 2, '&:last-child': { mb: 0 } }}>
                  {refAdIds.length > 1 && (
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                      {meta?.pageName || adId.slice(0, 8)}{frames.length > 0 ? ` · ${frames.length} shot${frames.length === 1 ? '' : 's'}` : ''}
                    </Typography>
                  )}
                  {(meta?.videoUrl || meta?.archivedVideoShareUrl || frames.length > 0) && (
                    // Video on the left (fixed by intrinsic aspect at 360px
                    // tall). Keyframes fill the remaining row width and
                    // wrap to additional rows when they don't fit; the
                    // wrapped area scrolls vertically inside a fixed
                    // height so the parent table isn't pushed wider.
                    // minWidth: 0 on the keyframe pane is required so flex
                    // doesn't grow the parent past the row's width.
                    <Box sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'flex-start',
                      // width:100% + minWidth:0 are both required: width
                      // anchors the row to the parent cell, minWidth:0 lets
                      // its flex children (specifically the keyframes pane)
                      // shrink below their intrinsic content width.
                      width: '100%',
                      minWidth: 0,
                    }}>
                      {(meta?.videoUrl || meta?.archivedVideoShareUrl) && (
                        <Box sx={{ flex: '0 0 auto' }}>
                          <FallbackVideo primary={meta.videoUrl} fallback={meta.archivedVideoShareUrl} controls preload="metadata"
                            style={{ height: 360, width: 'auto', borderRadius: 8, backgroundColor: '#000', display: 'block' }} />
                        </Box>
                      )}
                      {frames.length > 0 && (
                        <Box sx={{
                          flex: '1 1 0',
                          // minWidth:0 is the critical flexbox shrink-fix:
                          // without it the strip grows to fit all frames
                          // and pushes the table wider than the viewport.
                          minWidth: 0,
                          display: 'flex',
                          flexWrap: 'nowrap',
                          gap: 1,
                          overflowX: 'auto',
                          overflowY: 'hidden',
                          pb: 0.5,
                        }}>
                          {frames.map((kf) => (
                            <Box key={kf.shotOrder} sx={{ flex: '0 0 auto' }}>
                              <Box component="a" href={kf.url} target="_blank" rel="noreferrer"
                                sx={{
                                  display: 'block',
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: '#000',
                                  '&:hover': { borderColor: 'primary.main' },
                                }}>
                                <img src={kf.url} alt={`Shot ${kf.shotOrder}`}
                                  loading="lazy"
                                  style={{ height: 360, width: 'auto', objectFit: 'contain', display: 'block' }} />
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block', fontFamily: 'monospace', fontSize: '0.65rem' }}>
                                #{kf.shotOrder} · {kf.startTime.toFixed(1)}s–{kf.endTime.toFixed(1)}s
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </CollapsibleBlock>
      </>)}

      <Snackbar
        open={!!copyToastMsg}
        autoHideDuration={2500}
        onClose={() => setCopyToastMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={copyToastMsg || ''}
      />
    </Box>
  );
}

interface CollapsibleBlockProps {
  title: string;
  sub?: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function CollapsibleBlock({ title, sub, open, onToggle, children }: CollapsibleBlockProps) {
  return (
    <Box sx={{ mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#fff' }}>
      <Box onClick={onToggle}
        role="button" tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none', px: 1, py: 0.5 }}>
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggle(); }}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
        <Typography variant="caption" fontWeight={700}>{title}</Typography>
        {sub && (
          <Typography variant="caption" color="text.secondary">· {sub}</Typography>
        )}
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ p: 1.5, pt: 0 }}>{children}</Box>
      </Collapse>
    </Box>
  );
}
